/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isAlmostSameValueF32 from '@stdlib/number/float32/base/assert/is-almost-same-value';
import reim from './../../../../../float32/reim';


// MAIN //

/**
* Tests whether two single-precision complex floating-point numbers are approximately the same value within a specified number of ULPs (units in the last place).
*
* ## Notes
*
* -   The function implements the [SameValue Algorithm][ecma-262-same-value-algorithm], as specified in ECMAScript 5.
* -   In contrast to the strict equality operator `===`, `-0` and `+0` are distinguishable and `NaNs` are the same.
*
* [ecma-262-same-value-algorithm]: http://ecma-international.org/ecma-262/5.1/#sec-9.12
*
* @param {Complex64} z1 - first complex number
* @param {Complex64} z2 - second complex number
* @param {number} maxULP - maximum allowed ULP difference
* @returns {boolean} boolean indicating whether two single-precision complex floating-point numbers are approximately the same value within a specified number of ULPs
*
* @example
* import EPS from '@stdlib/constants/float32/eps';
* import Complex64 from '@stdlib/complex/float32/ctor';
*
* var z1 = new Complex64( 1.0, 3.0 );
* var z2 = new Complex64( 1.0+EPS, 3.0 );
*
* var bool = isAlmostSameValue( z1, z2, 0 );
* // returns false
*
* bool = isAlmostSameValue( z1, z2, 1 );
* // returns true
*/
function isAlmostSameValue( z1, z2, maxULP ) {
	var parts1 = reim( z1 );
	var parts2 = reim( z2 );
	return (
		isAlmostSameValueF32( parts1[ 0 ], parts2[ 0 ], maxULP ) &&
		isAlmostSameValueF32( parts1[ 1 ], parts2[ 1 ], maxULP )
	);
}


// EXPORTS //

export default isAlmostSameValue;
